/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useCallback, useEffect, useState } from "react";
import { NetworkStatus, useLazyQuery, useMutation } from "@apollo/client";
import { View } from "react-native";

import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { RootState, SelfObject } from "store/types";
import { ActionCreators } from "actions";

import {
  Button,
  EditFinishedSummitModal,
  LoadingDots,
  FadeInOutView,
  Text,
  UpduoButton,
} from "UpduoLibrary";
import {
  skaleWhite,
  skaleWebTextGray,
  skaleWebTextLightGray,
  skalePrimaryBlue,
  skaleRed,
  upduoPrimaryBlue,
} from "config/UpduoColors";
import SummitEntry from "./SummitEntry";
import DeleteSummitModal from "./DeleteSummitModal";
import EmptyCalendarSVG from "assets/empty-calendar.svg";
import {
  GET_STAFF_COHORTS,
  GET_STAFF_ACTIVE_SUMMIT_LIST,
  GET_STAFF_PAST_SUMMIT_LIST,
} from "graphql/internal/Queries";
import { EDIT_SUMMIT } from "graphql/internal/Mutations";
import {
  EditSummitMutationResponse,
  GetStaffCohortsQuery,
  GetStaffActiveSummitListQuery,
  GetStaffPastSummitListQuery,
  SummitType,
} from "graphql/internal/generated";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import { getNodes, sortByKey } from "config/Utilities";

export default function SummitAdminListRoute({ history }) {
  const dispatch = useDispatch();
  const selfObject: SelfObject = useSelector(
    (state: RootState) => state.selfObject
  );
  const authToken = useSelector((state: RootState) => state.authToken);

  const internalPermission =
    selfObject.primaryEmail?.endsWith("@upduo.com") ||
    selfObject.primaryEmail?.endsWith("@arcade.co");

  const [editFinishedSummitModalVisible, setEditFinishedSummitModalVisible] =
    useState(false);
  const [deleteSummitModalVisible, setDeleteSummitModalVisible] =
    useState(false);
  const [unassignedUserCount, setUnassignedUserCount] = useState(0);
  const [unassignedUserproxyCount, setUnassignedUserproxyCount] = useState(0);

  // The current summit that the user has clicked for edit/delete modal
  const [deleteSummitName, setDeleteSummitName] = useState("");
  const [deleteSummitId, setDeleteSummitId] = useState("");
  const [summitIdToEdit, setSummitIdToEdit] = useState<string | null>(null);
  const [cohortAlert, setCohortAlert] = useState("");

  const canEdit = Boolean(authToken);

  const [getStaffCohortList, { loading: getStaffCohortListLoading }] =
    useLazyQuery<GetStaffCohortsQuery>(GET_STAFF_COHORTS, {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      onCompleted: ({ staffCohorts }) => {
        let cohorts = getNodes(staffCohorts);
        const cohortUserCounts = cohorts.reduce(
          (accumulator, cohort) => accumulator + cohort.userCount,
          0
        );
        const cohortUserproxyCounts = cohorts.reduce(
          (accumulator, cohort) => accumulator + cohort.userproxyCount,
          0
        );
        const newUnassignedUserCount =
          selfObject.organizationUserCount - cohortUserCounts;
        const newUnassignedUserproxyCount =
          selfObject.organizationUserproxyCount - cohortUserproxyCounts;
        setUnassignedUserCount(newUnassignedUserCount);
        setUnassignedUserproxyCount(newUnassignedUserproxyCount);
        // Find all cohorts that are not in any active summits (including undefined cohort)
        let hasUndefinedCohortSummit = false;
        const usedCohorts = new Set();
        if (cohorts.length > 0) {
          for (let i = 0; i < cohorts.length; i++) {
            for (let j = 0; j < activeSummits.length; j++) {
              if (activeSummits[j].cohorts.length > 0) {
                for (let k = 0; k < activeSummits[j].cohorts.length; k++) {
                  if (cohorts[i].id === activeSummits[j].cohorts[k].id) {
                    usedCohorts.add(cohorts[i].id);
                  }
                }
              } else {
                hasUndefinedCohortSummit = true;
              }
            }
          }
        } else if (activeSummits.length > 0) {
          hasUndefinedCohortSummit = true;
        }
        cohorts = cohorts.filter((cohort) => !usedCohorts.has(cohort.id));
        if (!hasUndefinedCohortSummit) {
          cohorts.push({
            id: "undefined",
            name: "DEFAULT UNDEFINED COHORT",
            userCount: newUnassignedUserCount,
            userproxyCount: newUnassignedUserproxyCount,
          });
        }
        setCohortAlert(
          cohorts.length > 0
            ? `There exists cohort(s) not assigned to any live event(s):\n${cohorts
                .map(
                  (cohort) =>
                    `${cohort?.name} (${cohort?.userCount} members, ${cohort?.userproxyCount} not registered)`
                )
                .join("\n")}`
            : ""
        );
      },
    });

  const [
    getStaffActiveSummitList,
    {
      data: getStaffActiveSummitListData,
      loading: getStaffActiveSummitListLoading,
    },
  ] = useLazyQuery<GetStaffActiveSummitListQuery>(
    GET_STAFF_ACTIVE_SUMMIT_LIST,
    {
      // if this isn't specified, the onCompleted will only run once, since subsequent calls will be cached,
      // also needed for the mutation onCompleted
      fetchPolicy: "network-only",
      onCompleted: () => {
        getStaffCohortList();
      },
    }
  );

  const [
    getStaffPastSummitList,
    {
      data: getStaffPastSummitListData,
      loading: getStaffPastSummitListLoading,
      fetchMore,
      networkStatus,
    },
  ] = useLazyQuery<GetStaffPastSummitListQuery>(GET_STAFF_PAST_SUMMIT_LIST, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [deactivateSummit, {}] = useMutation<EditSummitMutationResponse>(
    EDIT_SUMMIT,
    {
      onCompleted: () => {
        setDeleteSummitModalVisible(false);
        getStaffActiveSummitList({
          variables: { isStaff: true },
        });
        getStaffPastSummitList({
          variables: { isStaff: true },
        });
      },
    }
  );

  const activeSummits = sortByKey(
    getNodes(getStaffActiveSummitListData?.userActiveSummits),
    "startDate"
  );

  if (activeSummits.length > 0) {
    dispatch(
      ActionCreators.setMostRecentMatchAlgorithm(
        activeSummits[0].matchAlgorithm
      )
    );
  }

  const pastSummits = sortByKey(
    getNodes(getStaffPastSummitListData?.userPastSummits),
    "startDate"
  );

  useEffect(() => {
    getStaffActiveSummitList({
      variables: { isStaff: true },
    });
    getStaffPastSummitList({
      variables: { isStaff: true },
    });
  }, [authToken]);

  const _handleDeleteSummit = useCallback(() => {
    deactivateSummit({
      variables: {
        editSummitMutationRequest: {
          summitId: deleteSummitId,
          isActive: false,
        },
      },
    });
  }, [deleteSummitId]);

  if (!selfObject.isStaff) {
    return <Redirect to={"/"} />;
  }

  const _handleEditFinishedSummitModalDismiss = () => {
    setEditFinishedSummitModalVisible(false);
    UpduoAnalytics.logEvent("user-edit-finished-summit-modal-back");
  };

  const _handleEditEndedSummit = () => {
    history.push(`/events/detail/edit/${summitIdToEdit}`);
    UpduoAnalytics.logEvent("user-edit-summit-modal-back", {
      summitId: summitIdToEdit,
    });
  };

  const _handleDeleteSummitModalDismiss = () => {
    setDeleteSummitModalVisible(false);
    UpduoAnalytics.logEvent("user-delete-summit-modal-back");
  };

  const _handleEditUpcomingSummit = (id: string) => {
    setSummitIdToEdit(id);
    history.push(`/events/detail/edit/${id}`);
  };

  const _handleShowEditFinishedSummitModal = (id: string) => {
    setSummitIdToEdit(id);
    setEditFinishedSummitModalVisible(true);
  };

  const _handleShowDeleteSummitModal = (name: string, id: string) => {
    setDeleteSummitName(name);
    setDeleteSummitId(id);
    setDeleteSummitModalVisible(true);
  };

  const loadNextPage = () => {
    if (
      !getStaffPastSummitListLoading &&
      networkStatus !== NetworkStatus.fetchMore &&
      getStaffPastSummitListData?.userPastSummits?.pageInfo?.hasNextPage
    ) {
      fetchMore({
        variables: {
          isStaff: true,
          cursor: getStaffPastSummitListData.userPastSummits.pageInfo.endCursor,
        },
      });
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <View>
        <View
          style={{
            flex: 1,
            margin: 60,
            paddingVertical: 32,
            backgroundColor: skaleWhite,
            borderRadius: 12,
            alignItems: "center",
            minWidth: 700,
          }}
        >
          {getStaffCohortListLoading ||
          getStaffActiveSummitListLoading ||
          getStaffPastSummitListLoading ? (
            <View style={{ alignSelf: "center", marginTop: 20 }}>
              <LoadingDots />
            </View>
          ) : (
            <View style={{ marginLeft: 10, paddingTop: 10 }}>
              {internalPermission ? (
                <Text style={{ color: skaleRed }}>{cohortAlert}</Text>
              ) : null}
              <View
                style={{
                  width: "80vw",
                  flexDirection: "row",
                  flex: 1,
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                <Text
                  style={{
                    fontSize: 24,
                    fontWeight: "bold",
                  }}
                >
                  {"Live Events"}
                </Text>
                {canEdit ? (
                  <Button
                    borderColor={skalePrimaryBlue}
                    color={upduoPrimaryBlue}
                    marginRight={20}
                    onPress={() => {
                      UpduoAnalytics.logEvent("user-summit-new", {
                        isClone: false,
                      });
                      history.push("/events/detail/edit/new");
                    }}
                    text={"+ Schedule Event"}
                  />
                ) : null}
              </View>
              {activeSummits.length > 0 ? (
                activeSummits.map((summit) => (
                  <SummitEntry
                    key={summit.id}
                    summit={summit as SummitType}
                    canEdit={canEdit}
                    onPress={() => {
                      history.push(`/events/detail/${summit.id}`);
                    }}
                    onPressClone={() => {
                      history.push(`/events/detail/edit/new/${summit.id}`);
                    }}
                    editSummit={_handleEditUpcomingSummit}
                    showEditModal={_handleShowEditFinishedSummitModal}
                    showDeleteModal={_handleShowDeleteSummitModal}
                    unassignedUserCount={unassignedUserCount}
                    unassignedUserproxyCount={unassignedUserproxyCount}
                  />
                ))
              ) : (
                <View style={{ alignItems: "center" }}>
                  <img
                    src={EmptyCalendarSVG}
                    style={{ width: 300 }}
                    alt={"empty calendar"}
                  />
                  <Text style={{ color: skaleWebTextLightGray }}>
                    {"No upcoming events; create a new one!"}
                  </Text>
                </View>
              )}
              {pastSummits.length > 0 ? (
                <Text
                  style={{
                    fontWeight: "bold",
                    fontSize: 20,
                    marginTop: 20,
                    marginBottom: 20,
                    color: skaleWebTextGray,
                  }}
                >
                  {"Past Events"}
                </Text>
              ) : null}
              <FadeInOutView
                visible={pastSummits.length > 0}
                style={{
                  alignSelf: "flex-start",
                  justifyContent: "flex-start",
                  flexDirection: "row-reverse",
                  flexWrap: "wrap-reverse",
                  width: "80vw",
                }}
              >
                {pastSummits.slice(0).map((summit) => (
                  <SummitEntry
                    key={summit.id}
                    summit={summit as SummitType}
                    canEdit={canEdit}
                    onPress={() => {
                      history.push(`/events/detail/${summit.id}`);
                    }}
                    onPressClone={() => {
                      history.push(`/events/detail/edit/new/${summit.id}`);
                    }}
                    editSummit={_handleEditUpcomingSummit}
                    showEditModal={_handleShowEditFinishedSummitModal}
                    showDeleteModal={_handleShowDeleteSummitModal}
                    unassignedUserCount={unassignedUserCount}
                    unassignedUserproxyCount={unassignedUserproxyCount}
                  />
                ))}
              </FadeInOutView>
              {getStaffPastSummitListData?.userPastSummits.pageInfo
                .hasNextPage ? (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-around",
                    marginTop: 20,
                  }}
                >
                  <UpduoButton
                    text={"Load More"}
                    onPress={loadNextPage}
                    loading={getStaffPastSummitListLoading}
                    style={{ paddingHorizontal: 25 }}
                  />
                </View>
              ) : null}
            </View>
          )}
        </View>
      </View>
      <DeleteSummitModal
        onDismiss={_handleDeleteSummitModalDismiss}
        onDeleteSummitConfirm={_handleDeleteSummit}
        summitName={deleteSummitName}
        isVisible={deleteSummitModalVisible}
      />
      <EditFinishedSummitModal
        onDismiss={_handleEditFinishedSummitModalDismiss}
        onEditEndedSummitConfirm={_handleEditEndedSummit}
        isVisible={editFinishedSummitModalVisible}
      />
    </View>
  );
}
